import React, { useEffect, useRef, useState } from 'react';
import {
  Map,
  View,
  Feature,
  Overlay, 
} from 'ol';
import { fromLonLat } from 'ol/proj';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Point from 'ol/geom/Point';
import Style from 'ol/style/Style';
import Icon from 'ol/style/Icon';
import { defaults as defaultControls, Zoom } from 'ol/control';
import 'ol/ol.css';

import bus from '../../../assets/ship.png';

import { Box, ToggleButton, Tooltip } from '@mui/material';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import haversine from 'haversine';
import { useTheme } from '@mui/material/styles';

function Openlayer({ position, userLocation }) {
  const mapEl = useRef(null);
  const [map, setMap] = useState(null);
  const [follow, setFollow] = useState(false);
  const theme = useTheme();

  const liveSrc = useRef(new VectorSource()).current;
  const userSrc = useRef(new VectorSource()).current;
  const view = useRef(new View({ zoom: 5, maxZoom: 20 })).current;
  const fromMeElRef = useRef(null);

  /* ---------- MAP INIT ---------- */
  useEffect(() => {
    if (!mapEl.current) return;

    const olMap = new Map({
      target: mapEl.current,
      layers: [
        new TileLayer({ source: new OSM() }),
        new VectorLayer({ source: userSrc, zIndex: 13 }),
        new VectorLayer({ source: liveSrc, zIndex: 14 }),
      ],
      view,
      controls: defaultControls({
        attribution: false,
        zoom: false,
      }).extend([new Zoom({ className: 'mui-zoom-control' })]),
    });

    /* ---- MUI-styled zoom ---- */
    setTimeout(() => {
      const zoomEl = document.querySelector('.mui-zoom-control');
      if (!zoomEl) return;
      Object.assign(zoomEl.style, {
        position: 'absolute',
        bottom: '20px',
        right: '20px',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '12px',
        boxShadow: '0 3px 6px rgba(0,0,0,0.25)',
        background: theme.palette.background.paper,
        overflow: 'hidden',
        zIndex: 1000,
      });
      zoomEl.querySelectorAll('button').forEach((btn) => {
        Object.assign(btn.style, {
          width: '40px',
          height: '40px',
          fontSize: '20px',
          border: 'none',
          cursor: 'pointer',
          color: theme.palette.text.primary,
          background: 'transparent',
          transition: 'all 0.25s ease',
        });
        btn.addEventListener('mouseenter', () => {
          btn.style.background = theme.palette.primary.main;
          btn.style.color = theme.palette.primary.contrastText;
        });
        btn.addEventListener('mouseleave', () => {
          btn.style.background = 'transparent';
          btn.style.color = theme.palette.text.primary;
        });
      });
    }, 300);

    /* ---- Distance-From-Me overlay ---- */
    const fromMeEl = document.createElement('div');
    fromMeEl.style.cssText = `
      position:absolute;top:78px;left:16px;
      background:rgba(33,150,243,0.9);color:white;padding:8px 12px;
      border-radius:8px;font-family:Roboto,sans-serif;
      font-size:14px;font-weight:500;z-index:1000;
      pointer-events:none;backdrop-filter:blur(4px);
      box-shadow:0 2px 6px rgba(0,0,0,0.2);min-width:140px;
    `;
    olMap.getViewport().appendChild(fromMeEl);
    fromMeElRef.current = fromMeEl;
    olMap.addOverlay(
      new Overlay({ element: fromMeEl, positioning: 'top-left', stopEvent: false })
    );

    setMap(olMap);

    return () => {
      olMap.setTarget(undefined);
      if (fromMeEl && fromMeEl.parentNode) {
        fromMeEl.parentNode.removeChild(fromMeEl);
      }
    };
  }, [theme]);

  /* ---------- LIVE POSITION + USER + DISTANCE ---------- */
  useEffect(() => {
    if (!map || !position) return;

    const lat = 'latitude' in position ? position.latitude : position.lat;
    const lng = 'longitude' in position ? position.longitude : position.lng;
    if (lat == null || lng == null) return;

    const coord = fromLonLat([lng, lat]);   // <-- uses imported fromLonLat

    // Bus marker
    liveSrc.clear();
    const busF = new Feature({ geometry: new Point(coord) });
    busF.setStyle(
      new Style({ image: new Icon({ anchor: [0.5, 1], src: bus, scale: 0.4 }) })
    );
    liveSrc.addFeature(busF);

    // User marker + distance
    userSrc.clear();
    if (
      userLocation &&
      typeof userLocation.latitude === 'number' &&
      typeof userLocation.longitude === 'number'
    ) {
      const userCoord = fromLonLat([userLocation.longitude, userLocation.latitude]);
      const userF = new Feature({ geometry: new Point(userCoord) });
      userF.setStyle(
        new Style({
          image: new Icon({
            anchor: [0.5, 0.5],
            src: 'data:image/svg+xml;charset=utf- ' +
              encodeURIComponent(`
                <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="9" fill="#1976d2" stroke="#fff" stroke-width="3"/>
                </svg>
              `),
            scale: 1,
          }),
        })
      );
      userSrc.addFeature(userF);

      const dist = haversine(
        { latitude: userLocation.latitude, longitude: userLocation.longitude },
        { latitude: lat, longitude: lng },
        { unit: 'meter' }
      );
      const fmt = (m) =>
        m > 1000 ? (m / 1000).toFixed(1) + ' km' : Math.round(m) + ' m';

      if (fromMeElRef.current) {
        fromMeElRef.current.innerHTML = userLocation
          ? `<div style="font-size:15px;font-weight:600;color:white;">
               From Me: <strong style="color:#fff;">${fmt(dist)}</strong>
             </div>`
          : '';
      }
    } else if (fromMeElRef.current) {
      fromMeElRef.current.innerHTML = '';
    }

    // Follow mode
    if (follow) {
      view.animate({ center: coord, zoom: 16, duration: 800 });
    }
  }, [position, userLocation, follow, map]);

  /* ---------- TOGGLE FOLLOW ---------- */
  const toggleFollow = () => {
    const next = !follow;
    setFollow(next);
    if (next && position) {
      const lat = 'latitude' in position ? position.latitude : position.lat;
      const lng = 'longitude' in position ? position.longitude : position.lng;
      if (lat != null && lng != null) {
        view.animate({
          center: fromLonLat([lng, lat]),
          zoom: 16,
          duration: 800,
        });
      }
    }
  };

  /* Auto-center on first position */
  useEffect(() => {
    if (map && position && !view.getCenter()) {
      const lat = 'latitude' in position ? position.latitude : position.lat;
      const lng = 'longitude' in position ? position.longitude : position.lng;
      if (lat != null && lng != null) {
        view.animate({ center: fromLonLat([lng, lat]), zoom: 15 });
      }
    }
  }, [map, position]);

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '90vh' }}>
      <Box
        ref={mapEl}
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: 1,
        }}
      />
      <Tooltip title={follow ? 'Following' : 'Follow vehicle'} placement="left">
        <ToggleButton
          value="follow"
          selected={follow}
          onChange={toggleFollow}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 10,
            bgcolor: 'background.paper',
            '&.Mui-selected': { bgcolor: 'primary.main', color: 'white' },
            '&:hover': { bgcolor: follow ? 'primary.dark' : 'grey.300' },
            borderRadius: 2,
            minWidth: 48,
            height: 48,
          }}
        >
          <LocationSearchingIcon />
        </ToggleButton>
      </Tooltip>
    </Box>
  );
}

export default Openlayer;